export type DateRangeValue = { from: Date | null; to: Date | null }

export type DateRangeEnd = keyof DateRangeValue

export const DATE_ERROR_MESSAGES = {
  invalidDate: 'תאריך לא תקין',
  disableFuture: 'לא ניתן לבחור תאריך עתידי',
  disablePast: 'לא ניתן לבחור תאריך שעבר',
  minDate: 'התאריך מוקדם מהמותר',
  maxDate: 'התאריך מאוחר מהמותר',
} as const

export const dateErrorMessage = (error: string | null | undefined) => {
  if (!error) return null
  return (
    DATE_ERROR_MESSAGES[error as keyof typeof DATE_ERROR_MESSAGES] ??
    DATE_ERROR_MESSAGES.invalidDate
  )
}

// new Date(2, ...) means 1902, which breaks a field while the year is typed.
const atTime = (date: Date, day: number, h = 0, m = 0, sec = 0, ms = 0) => {
  const out = new Date(2000, date.getMonth(), 1, h, m, sec, ms)
  out.setFullYear(date.getFullYear(), date.getMonth(), day)
  return out
}

export const startOfDay = (date: Date) => atTime(date, date.getDate())

export const endOfDay = (date: Date) =>
  atTime(date, date.getDate(), 23, 59, 59, 999)

export const addDays = (date: Date, days: number) =>
  atTime(date, date.getDate() + days)

export type RangeLimits = {
  minDate?: Date
  maxDate?: Date
  /** Longest window, counting both end days. */
  maxWindowDays?: number
}

export type AllowedDays = { minDate?: Date; maxDate?: Date }

const later = (a?: Date, b?: Date) => (a && b ? (a > b ? a : b) : (a ?? b))
const earlier = (a?: Date, b?: Date) => (a && b ? (a < b ? a : b) : (a ?? b))

/**
 * The days `end` may take, given the other end and the limits. The calendar
 * disables every day outside it and the typed-date check reads the same bounds.
 * "From" may not pass "to", and "to" may not precede "from".
 */
export const allowedRange = (
  end: DateRangeEnd,
  range: DateRangeValue,
  { minDate, maxDate, maxWindowDays }: RangeLimits = {}
): AllowedDays => {
  const span = maxWindowDays ? maxWindowDays - 1 : null
  if (end === 'from') {
    const { to } = range
    return {
      minDate: later(
        minDate,
        to && span !== null ? addDays(to, -span) : undefined
      ),
      maxDate: earlier(maxDate, to ? endOfDay(to) : undefined),
    }
  }
  const { from } = range
  return {
    minDate: later(minDate, from ? startOfDay(from) : undefined),
    maxDate: earlier(
      maxDate,
      from && span !== null ? endOfDay(addDays(from, span)) : undefined
    ),
  }
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/

/** Reads the day of a `YYYY-MM-DD` string (a time after it is ignored) as a local date. */
export const parseDay = (text: string | null | undefined): Date | null => {
  const match = text ? DAY_PATTERN.exec(text) : null
  if (!match) return null
  const seed = new Date(2000, Number(match[2]) - 1, 1)
  seed.setFullYear(Number(match[1]))
  const date = atTime(seed, Number(match[3]))
  return date.getMonth() === Number(match[2]) - 1 ? date : null
}

/** The local day as `YYYY-MM-DD`, or '' for no date. */
export const formatDay = (date: Date | null): string =>
  date
    ? `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    : ''
